import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  IconButton,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CardTitleRow from './CardTitleRow';
import LoadErrorScreen from './LoadErrorScreen';
import './ItemCard.css';
import './ItemList.css';
import { getProductDeliveryCaption } from '../utils/productDelivery';

const getPhotoUrl = (photo) => {
  if (!photo || typeof photo !== 'string') return null;
  const t = photo.trim();
  if (!t) return null;
  if (t.startsWith('http://') || t.startsWith('https://')) return t;
  if (t.startsWith('/')) return `${process.env.REACT_APP_SERVER_URL}${t}`;
  return `${process.env.REACT_APP_SERVER_URL}/${t}`;
};

const getFirstPhoto = (product) => {
  const photos = product.photos;
  if (!photos || !Array.isArray(photos) || photos.length === 0) return null;
  for (const p of photos) {
    const url = getPhotoUrl(p);
    if (url) return url;
  }
  return null;
};

const ProductsPage = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [branchAddressByKey, setBranchAddressByKey] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [cartProductIds, setCartProductIds] = useState(new Set());
  const [addingCartId, setAddingCartId] = useState(null);

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    Promise.all([
      fetch(`${process.env.REACT_APP_SERVER_URL}/api/products`).then((r) => {
        if (!r.ok) throw new Error('Ошибка загрузки товаров');
        return r.json();
      }),
      fetch(`${process.env.REACT_APP_SERVER_URL}/api/admin/branches/public`).then((r) =>
        r.ok ? r.json() : []
      ),
    ])
      .then(([data, branchList]) => {
        setProducts(Array.isArray(data) ? data : []);
        const map = {};
        (Array.isArray(branchList) ? branchList : []).forEach((b) => {
          const key = b.branchId || b._id;
          if (key) map[key] = [b.city, b.address].filter(Boolean).join(', ') || key;
        });
        setBranchAddressByKey(map);
      })
      .catch(() => {
        setLoadError(true);
        setProducts([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${process.env.REACT_APP_SERVER_URL}/api/cart`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : { items: [] })
      .then((data) => {
        const ids = (data.items || [])
          .filter((i) => i.productId && (i.productId._id || i.productId))
          .map((i) => String(i.productId._id || i.productId));
        setCartProductIds(new Set(ids));
      })
      .catch(() => {});
  }, []);

  const handleAddToCart = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    const token = localStorage.getItem('token');
    if (!token) {
      if (window.confirm('Для добавления в корзину нужно войти. Перейти на страницу входа?')) {
        navigate('/login', { state: { redirectTo: `${location.pathname}${location.search}` } });
      }
      return;
    }
    if (addingCartId || cartProductIds.has(String(product._id))) return;
    setAddingCartId(product._id);
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/cart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ productNumber: product.productNumber, quantity: 1 }),
      });
      if (res.ok) setCartProductIds((prev) => new Set([...prev, String(product._id)]));
    } catch (err) {
      console.error('Ошибка добавления в корзину:', err);
    } finally {
      setAddingCartId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: '#8B7D6B' }} />
      </Box>
    );
  }

  if (loadError) {
    return <LoadErrorScreen />;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', px: 0.25, mx: 0, mt: 2 }}>
      <Grid container spacing={1} className="items-grid-container" sx={{ px: 0.5, overflowX: 'hidden', maxWidth: '100%' }}>
        {products.length > 0 ? (
          products.map((product, index) => {
            const photoUrl = getFirstPhoto(product) || 'https://via.placeholder.com/300';
            const isAboveFold = index < 8;
            const deliveryCaption = getProductDeliveryCaption(product, branchAddressByKey);
            return (
              <Grid item xs={6} sm={6} md={4} lg={true} key={product._id} className="item-grid-item">
                <Card
                  component={Link}
                  to={`/products/${product.productNumber}`}
                  className="card-container"
                  sx={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', height: '100%' }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        zIndex: 1,
                        backgroundColor: '#2e7d32',
                        color: '#fff',
                        px: 1,
                        py: 0.25,
                        borderRadius: '8px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      Продажа
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleAddToCart(e, product)}
                      disabled={!!addingCartId}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 1,
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        '&:hover': { backgroundColor: '#fff' },
                      }}
                      aria-label="В корзину"
                    >
                      <ShoppingCartIcon sx={{ color: cartProductIds.has(String(product._id)) ? '#d32f2f' : undefined }} />
                    </IconButton>
                    <div className="slider-image-container">
                      <img
                        src={photoUrl}
                        alt={product.title || 'Товар'}
                        className="card-image"
                        loading={isAboveFold ? 'eager' : 'lazy'}
                        decoding="async"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/300'; }}
                      />
                    </div>
                  </Box>
                  <CardContent className="card-content">
                    <CardTitleRow type="product" entityId={product._id}>
                      {product.title}
                    </CardTitleRow>
                    <Typography className="card-price" sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '0.85rem' }}>
                      {product.price != null ? `${Number(product.price).toLocaleString('ru-RU')} ₽` : 'Цена по запросу'}
                    </Typography>
                    {deliveryCaption && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', mt: 0.5 }}>
                        {deliveryCaption}
                      </Typography>
                    )}
                    {product.condition === 'used' && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Б/у
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        ) : (
          <Typography className="no-items-message" sx={{ gridColumn: '1 / -1' }}>
            Пока нет товаров
          </Typography>
        )}
      </Grid>
    </Box>
  );
};

export default ProductsPage;
